import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Progress } from '../ui/progress';
import { 
  Plus, 
  AlertCircle, 
  Calendar as CalendarIcon, 
  DollarSign, 
  Building2, 
  Mail, 
  Phone,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  BarChart3,
  Users,
  ShieldCheck,
  RefreshCw
} from 'lucide-react';
import { format, differenceInDays, addDays, addMonths } from 'date-fns';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { toast } from 'sonner';
import { ScrollArea } from '../ui/scroll-area';

interface License {
  id: string;
  companyId: string;
  companyName: string;
  vendor: string;
  productName: string;
  licenseType: string;
  quantity: number;
  purchaseDate: string;
  expirationDate: string;
  cost: number;
  currency: string;
  renewalStatus: 'active' | 'expiring_soon' | 'contacted' | 'quoted' | 'approved' | 'renewed' | 'expired' | 'cancelled';
  utilizationRate?: number; // Percentage of licenses actually used
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  managedBy: string;
  managedByName?: string;
  lastContactDate?: string;
  nextFollowUpDate?: string;
  renewalProbability?: number; // 0-100
  purchaseOrderNumber?: string;
  invoiceNumber?: string;
  complianceStatus?: 'compliant' | 'warning' | 'non_compliant';
  created_at: string;
  updated_at: string;
}

interface RenewalActivity {
  id: string;
  licenseId: string;
  activityType: 'email' | 'call' | 'meeting' | 'quote_sent' | 'status_change' | 'note';
  description: string;
  createdBy: string;
  createdByName: string;
  created_at: string;
}

/** A record from the firm's license portfolio (`GET /license/licenses`, LicenseManagementPro's shape). */
export interface PortfolioLicense {
  id: string;
  clientCompanyId?: string;
  clientCompanyName?: string;
  productName?: string;
  vendor?: string;
  licenseType?: string;
  quantity?: number;
  startDate?: string;
  expirationDate: string;
  annualValue?: number;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  status?: string;
  renewalProbability?: number;
  pipelineStage?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

const STAGE_TO_RENEWAL: Record<string, License['renewalStatus']> = {
  contacted: 'contacted',
  quoted: 'quoted',
  negotiating: 'quoted',
  committed: 'approved',
  renewed: 'renewed',
  lost: 'cancelled',
};

/**
 * The dashboard's view of a portfolio record (RESTORE_PLAN R3.3: this screen used
 * to show built-in sample licenses). The renewal status comes from the pipeline
 * stage, else from the expiration date.
 */
export function fromPortfolioLicense(l: PortfolioLicense, now: Date = new Date()): License {
  const days = differenceInDays(new Date(l.expirationDate), now);
  const byDate: License['renewalStatus'] = days < 0 ? 'expired' : days <= 90 ? 'expiring_soon' : 'active';
  return {
    id: l.id,
    companyId: l.clientCompanyId || '',
    companyName: l.clientCompanyName || 'Unassigned',
    vendor: l.vendor || '',
    productName: l.productName || '',
    licenseType: l.licenseType || '',
    quantity: Number(l.quantity) || 0,
    purchaseDate: l.startDate || '',
    expirationDate: l.expirationDate,
    cost: Number(l.annualValue) || 0,
    currency: 'USD',
    renewalStatus: (l.pipelineStage && STAGE_TO_RENEWAL[l.pipelineStage]) || byDate,
    contactName: l.contactName,
    contactEmail: l.contactEmail,
    contactPhone: l.contactPhone,
    notes: l.notes,
    managedBy: l.createdBy || '',
    renewalProbability: l.renewalProbability,
    created_at: l.createdAt || '',
    updated_at: l.updatedAt || '',
  };
}

/** The dashboard's Export: one CSV row per license. */
export function licensesCsv(licenses: License[]): string {
  const cell = (v: unknown) => {
    const t = v === undefined || v === null ? '' : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const header = ['Client', 'Vendor', 'Product', 'Type', 'Quantity', 'Expires', 'Annual value', 'Renewal status', 'Contact', 'Contact email'];
  const rows = licenses.map((l) => [l.companyName, l.vendor, l.productName, l.licenseType, l.quantity, l.expirationDate, l.cost, l.renewalStatus, l.contactName, l.contactEmail].map(cell).join(','));
  return [header.join(','), ...rows].join('\n');
}

interface LicenseManagerProps {
  accessToken: string;
  firmId: string;
  currentUser: any;
  viewMode?: 'dashboard' | 'full'; // Dashboard shows overview, full shows all details
  /** The firm's portfolio (Firm › Licenses loads it once for this dashboard and the manager below it). */
  licenses?: PortfolioLicense[];
  /** "View All Licenses" and "Manage": go to the full manager. */
  onViewAll?: () => void;
}

export function LicenseManager({ accessToken, firmId, currentUser, viewMode = 'dashboard', licenses: portfolio = [], onViewAll }: LicenseManagerProps) {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [filteredLicenses, setFilteredLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterExpiringDays, setFilterExpiringDays] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const mapped = portfolio.map((l) => fromPortfolioLicense(l));
    setLicenses(mapped);
    setFilteredLicenses(mapped);
    setLoading(false);
  }, [portfolio]);

  const exportCsv = () => {
    const blob = new Blob([licensesCsv(filteredLicenses)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `licenses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter licenses
  useEffect(() => {
    let filtered = licenses;

    if (searchTerm) {
      filtered = filtered.filter(lic => 
        lic.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lic.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lic.contactName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterVendor !== 'all') {
      filtered = filtered.filter(lic => lic.vendor === filterVendor);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(lic => lic.renewalStatus === filterStatus);
    }

    if (filterExpiringDays !== 'all') {
      const days = parseInt(filterExpiringDays);
      filtered = filtered.filter(lic => {
        const daysUntilExpiry = differenceInDays(new Date(lic.expirationDate), new Date());
        return daysUntilExpiry <= days && daysUntilExpiry >= 0;
      });
    }

    setFilteredLicenses(filtered);
  }, [searchTerm, filterVendor, filterStatus, filterExpiringDays, licenses]);

  const getDaysUntilExpiry = (expirationDate: string) => {
    return differenceInDays(new Date(expirationDate), new Date());
  };

  const getExpiryBadge = (license: License) => {
    const days = getDaysUntilExpiry(license.expirationDate);
    
    if (days < 0) {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Expired</Badge>;
    } else if (days <= 30) {
      return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />{days}d</Badge>;
    } else if (days <= 60) {
      return <Badge variant="outline" className="gap-1 border-orange-500 text-orange-600"><Clock className="h-3 w-3" />{days}d</Badge>;
    } else if (days <= 90) {
      return <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-600"><Clock className="h-3 w-3" />{days}d</Badge>;
    }
    return <Badge variant="outline" className="gap-1"><CheckCircle2 className="h-3 w-3" />{days}d</Badge>;
  };

  const getStatusBadge = (status: License['renewalStatus']) => {
    const statusConfig = {
      active: { variant: 'default' as const, label: 'Active' },
      expiring_soon: { variant: 'outline' as const, label: 'Expiring Soon' },
      contacted: { variant: 'secondary' as const, label: 'Contacted' },
      quoted: { variant: 'secondary' as const, label: 'Quote Sent' },
      approved: { variant: 'default' as const, label: 'Approved' },
      renewed: { variant: 'default' as const, label: 'Renewed' },
      expired: { variant: 'destructive' as const, label: 'Expired' },
      cancelled: { variant: 'outline' as const, label: 'Canceled' }
    };
    const config = statusConfig[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getComplianceBadge = (status?: License['complianceStatus']) => {
    if (!status) return null;
    const config = {
      compliant: { variant: 'default' as const, icon: CheckCircle2, label: 'Compliant' },
      warning: { variant: 'outline' as const, icon: AlertCircle, label: 'Warning' },
      non_compliant: { variant: 'destructive' as const, icon: XCircle, label: 'Non-Compliant' }
    };
    const { variant, icon: Icon, label } = config[status];
    return <Badge variant={variant} className="gap-1"><Icon className="h-3 w-3" />{label}</Badge>;
  };

  const calculateMetrics = () => {
    const totalValue = licenses.reduce((sum, lic) => sum + lic.cost, 0);
    const expiringIn30 = licenses.filter(lic => {
      const days = getDaysUntilExpiry(lic.expirationDate);
      return days >= 0 && days <= 30;
    });
    const expiringIn60 = licenses.filter(lic => {
      const days = getDaysUntilExpiry(lic.expirationDate);
      return days > 30 && days <= 60;
    });
    const expiringIn90 = licenses.filter(lic => {
      const days = getDaysUntilExpiry(lic.expirationDate);
      return days > 60 && days <= 90;
    });
    const expired = licenses.filter(lic => getDaysUntilExpiry(lic.expirationDate) < 0);
    // Utilization is shown only when some license records it (portfolio records do not yet)
    const withUtilization = licenses.filter(lic => typeof lic.utilizationRate === 'number');
    const avgUtilization = withUtilization.length
      ? withUtilization.reduce((sum, lic) => sum + (lic.utilizationRate || 0), 0) / withUtilization.length
      : null;
    const nonCompliant = licenses.filter(lic => lic.complianceStatus === 'non_compliant');

    return {
      totalValue,
      totalLicenses: licenses.length,
      expiringIn30: expiringIn30.length,
      expiringIn60: expiringIn60.length,
      expiringIn90: expiringIn90.length,
      expired: expired.length,
      avgUtilization: avgUtilization === null ? null : Math.round(avgUtilization),
      nonCompliant: nonCompliant.length,
      expiringIn30Value: expiringIn30.reduce((sum, lic) => sum + lic.cost, 0),
      expiringIn60Value: expiringIn60.reduce((sum, lic) => sum + lic.cost, 0),
      expiringIn90Value: expiringIn90.reduce((sum, lic) => sum + lic.cost, 0)
    };
  };

  const metrics = calculateMetrics();

  if (viewMode === 'dashboard') {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                License Management Dashboard
              </CardTitle>
              <CardDescription>
                Expirations, renewals and portfolio value across your clients
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={filteredLicenses.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button onClick={() => (onViewAll ? onViewAll() : setActiveTab('overview'))} size="sm">
                View All Licenses
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Total Portfolio Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">${metrics.totalValue.toLocaleString()}</div>
                <p className="text-muted-foreground">{metrics.totalLicenses} licenses</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Expiring in 30 Days</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl text-red-600">{metrics.expiringIn30}</div>
                <p className="text-muted-foreground">${metrics.expiringIn30Value.toLocaleString()} at risk</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Avg Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">{metrics.avgUtilization === null ? '—' : `${metrics.avgUtilization}%`}</div>
                {metrics.avgUtilization === null
                  ? <p className="text-muted-foreground">Not recorded yet</p>
                  : <Progress value={metrics.avgUtilization} className="mt-2" />}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Compliance Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl text-red-600">{metrics.nonCompliant}</div>
                <p className="text-muted-foreground">{metrics.expired} expired</p>
              </CardContent>
            </Card>
          </div>

          {/* Urgent Actions */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Bell className="h-4 w-4 text-red-500" />
              Urgent Actions Required
            </h3>
            
            <div className="space-y-2">
              {!filteredLicenses.some(lic => getDaysUntilExpiry(lic.expirationDate) <= 30 || lic.complianceStatus === 'non_compliant') && (
                <p className="text-muted-foreground" data-testid="no-urgent-licenses">Nothing expires in the next 30 days.</p>
              )}
              {filteredLicenses
                .filter(lic => getDaysUntilExpiry(lic.expirationDate) <= 30 || lic.complianceStatus === 'non_compliant')
                .sort((a, b) => getDaysUntilExpiry(a.expirationDate) - getDaysUntilExpiry(b.expirationDate))
                .slice(0, 5)
                .map(license => {
                  const days = getDaysUntilExpiry(license.expirationDate);
                  return (
                    <Card key={license.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">{license.companyName}</span>
                            {getExpiryBadge(license)}
                            {getComplianceBadge(license.complianceStatus)}
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">
                            {license.productName} • {license.quantity} licenses • ${license.cost.toLocaleString()}
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {license.contactEmail}
                            </span>
                            {license.lastContactDate && (
                              <span className="text-muted-foreground">
                                Last contact: {format(new Date(license.lastContactDate), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {license.contactEmail && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={`mailto:${license.contactEmail}?subject=${encodeURIComponent(`${license.productName} renewal`)}`}>
                                <Mail className="h-4 w-4 mr-1" />
                                Contact
                              </a>
                            </Button>
                          )}
                          <Button size="sm" onClick={() => (onViewAll ? onViewAll() : setSelectedLicense(license))}>
                            Manage
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                License Management
              </CardTitle>
              <CardDescription>
                Comprehensive license tracking and renewal management
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add License
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="renewals">Renewals Pipeline</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="forecasting">Forecasting</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {/* Filters */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder="Search licenses, companies, contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Select value={filterVendor} onValueChange={setFilterVendor}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    <SelectItem value="atlassian">Atlassian</SelectItem>
                    <SelectItem value="servicenow">ServiceNow</SelectItem>
                    <SelectItem value="ivanti">Ivanti</SelectItem>
                    <SelectItem value="bmc">BMC Helix</SelectItem>
                    <SelectItem value="freshservice">Freshservice</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterExpiringDays} onValueChange={setFilterExpiringDays}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Expiring..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    <SelectItem value="30">Next 30 Days</SelectItem>
                    <SelectItem value="60">Next 60 Days</SelectItem>
                    <SelectItem value="90">Next 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Licenses Table */}
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Utilization</TableHead>
                      <TableHead>Compliance</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLicenses.map((license) => (
                      <TableRow key={license.id}>
                        <TableCell>
                          <div>
                            <div className="font-semibold">{license.companyName}</div>
                            <div className="text-sm text-muted-foreground">{license.contactName}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div>{license.productName}</div>
                            <div className="text-sm text-muted-foreground">{license.licenseType}</div>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">{license.vendor}</TableCell>
                        <TableCell>{license.quantity}</TableCell>
                        <TableCell>${license.cost.toLocaleString()}</TableCell>
                        <TableCell>
                          <div>
                            <div className="text-sm">{format(new Date(license.expirationDate), 'MMM d, yyyy')}</div>
                            {getExpiryBadge(license)}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(license.renewalStatus)}</TableCell>
                        <TableCell>
                          {license.utilizationRate && (
                            <div className="space-y-1">
                              <div className="text-sm">{license.utilizationRate}%</div>
                              <Progress value={license.utilizationRate} className="w-20" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{getComplianceBadge(license.complianceStatus)}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedLicense(license)}
                          >
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="renewals" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Renewal Pipeline</CardTitle>
                  <CardDescription>Track renewal conversations and forecast revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {['Expiring Soon', 'Contacted', 'Quote Sent', 'Approved', 'Renewed'].map((stage, idx) => {
                      const stageCounts = {
                        'Expiring Soon': licenses.filter(l => l.renewalStatus === 'expiring_soon').length,
                        'Contacted': licenses.filter(l => l.renewalStatus === 'contacted').length,
                        'Quote Sent': licenses.filter(l => l.renewalStatus === 'quoted').length,
                        'Approved': licenses.filter(l => l.renewalStatus === 'approved').length,
                        'Renewed': licenses.filter(l => l.renewalStatus === 'renewed').length
                      };
                      const count = stageCounts[stage as keyof typeof stageCounts];
                      
                      return (
                        <Card key={stage}>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm">{stage}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-3xl mb-2">{count}</div>
                            <Button size="sm" variant="outline" className="w-full">
                              View Details
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="compliance">
              <Card>
                <CardHeader>
                  <CardTitle>Compliance Dashboard</CardTitle>
                  <CardDescription>Monitor license compliance and utilization</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Track licenses that are expired or have low utilization rates
                  </p>
                  {/* Compliance details would go here */}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analytics">
              <Card>
                <CardHeader>
                  <CardTitle>License Analytics</CardTitle>
                  <CardDescription>Insights and trends across your license portfolio</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    ROI analysis, cost trends, and vendor comparisons
                  </p>
                  {/* Analytics charts would go here */}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="forecasting">
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Forecasting</CardTitle>
                  <CardDescription>Project renewal revenue for upcoming quarters</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Based on renewal probability and historical data
                  </p>
                  {/* Forecasting details would go here */}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* License Detail Dialog */}
      {selectedLicense && (
        <LicenseDetailDialog
          license={selectedLicense}
          open={!!selectedLicense}
          onClose={() => setSelectedLicense(null)}
          accessToken={accessToken}
        />
      )}
    </div>
  );
}

// License Detail Dialog Component
function LicenseDetailDialog({ 
  license, 
  open, 
  onClose,
  accessToken 
}: { 
  license: License; 
  open: boolean; 
  onClose: () => void;
  accessToken: string;
}) {
  const [activeTab, setActiveTab] = useState('details');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {license.companyName} - {license.productName}
          </DialogTitle>
          <DialogDescription>
            License ID: {license.id}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">License Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Vendor:</span>
                    <span className="font-semibold capitalize">{license.vendor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Product:</span>
                    <span className="font-semibold">{license.productName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span>{license.licenseType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span>{license.quantity} licenses</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Annual Cost:</span>
                    <span className="font-semibold">${license.cost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Utilization:</span>
                    <span>{license.utilizationRate}%</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">Primary Contact</div>
                    <div className="font-semibold">{license.contactName}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${license.contactEmail}`} className="text-blue-600 hover:underline">
                      {license.contactEmail}
                    </a>
                  </div>
                  {license.contactPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{license.contactPhone}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Important Dates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Purchase Date:</span>
                    <span>{format(new Date(license.purchaseDate), 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expiration Date:</span>
                    <span className="font-semibold">{format(new Date(license.expirationDate), 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Days Until Expiry:</span>
                    <span className="font-semibold text-red-600">{differenceInDays(new Date(license.expirationDate), new Date())} days</span>
                  </div>
                  {license.lastContactDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Contact:</span>
                      <span>{format(new Date(license.lastContactDate), 'MMM d, yyyy')}</span>
                    </div>
                  )}
                  {license.nextFollowUpDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Next Follow-up:</span>
                      <span className="font-semibold">{format(new Date(license.nextFollowUpDate), 'MMM d, yyyy')}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Renewal Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Current Status:</span>
                    <Badge>{license.renewalStatus.replace('_', ' ')}</Badge>
                  </div>
                  {license.renewalProbability !== undefined && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-muted-foreground">Renewal Probability:</span>
                        <span className="font-semibold">{license.renewalProbability}%</span>
                      </div>
                      <Progress value={license.renewalProbability} />
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Compliance:</span>
                    {license.complianceStatus === 'compliant' && <Badge variant="default">Compliant</Badge>}
                    {license.complianceStatus === 'warning' && <Badge variant="outline">Warning</Badge>}
                    {license.complianceStatus === 'non_compliant' && <Badge variant="destructive">Non-Compliant</Badge>}
                  </div>
                  {license.purchaseOrderNumber && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">PO Number:</span>
                      <span>{license.purchaseOrderNumber}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {license.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{license.notes}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Activity Timeline</CardTitle>
                <CardDescription>Communication history and status changes</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Activity history would be displayed here</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Related Documents</CardTitle>
                <CardDescription>Quotes, invoices, purchase orders, and contracts</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Document management would be displayed here</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="forecast">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Renewal Forecast</CardTitle>
                <CardDescription>Projected renewal value and timeline</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Forecasting details would be displayed here</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button>
            <Mail className="h-4 w-4 mr-2" />
            Send Renewal Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}